import { Check, X, ClipboardCheck } from "lucide-react";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";
import { CollapsibleSection } from "../CollapsibleSection";

/**
 * Feb 2026 · Team visibility of the customer's personal departure/arrival
 * to-dos. Shows what the customer ticked off (Fenster, Alarm, Müll …) on
 * the Ab-/Anreise wizard before saving their absence. Read-only.
 *
 * Extrahiert aus `TeamCustomerDetail.jsx` (Refactor Feb 2026) —
 * Verhalten unverändert. Farben aus dem zentralen Palette-Modul.
 */
export function BookingChecklistPanel({ booking, isCurrent }) {
  const dep = booking?.departure_checklist || [];
  const arr = booking?.arrival_checklist || [];
  if (dep.length === 0 && arr.length === 0) return null;
  const totalItems = dep.length + arr.length;
  const doneItems = dep.filter((it) => it.done).length + arr.filter((it) => it.done).length;
  const allDone = totalItems > 0 && doneItems === totalItems;

  const Section = ({ title, items }) => {
    if (items.length === 0) return null;
    const done = items.filter((it) => it.done).length;
    const all = items.length;
    const pct = all === 0 ? 0 : Math.round((done / all) * 100);
    return (
      <div className="flex-1 min-w-[260px]" data-testid={`booking-checklist-${title.toLowerCase()}`}>
        <div className="flex items-baseline justify-between mb-2">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{title}</div>
          <div className="label-caps text-navy-dark/60">{done} / {all}</div>
        </div>
        <div
          className="h-1 w-full rounded-full overflow-hidden mb-3"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${pct}%`,
              background: done === all ? STATUS_COLORS.green : BRAND_COLORS.gold,
            }}
          />
        </div>
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={it.key}
              data-testid={`booking-check-${it.key}${it.done ? "-done" : ""}`}
              className="flex items-start gap-2 text-[13px] leading-snug"
            >
              <span
                className="mt-0.5 w-4 h-4 border rounded flex items-center justify-center shrink-0"
                style={it.done
                  ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
                  : { background: BRAND_COLORS.white, borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
                aria-hidden
              >
                {it.done
                  ? <Check className="w-2.5 h-2.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />
                  : <X className="w-2.5 h-2.5" style={{ color: withAlpha(BRAND_COLORS.navy, 0.30) }} strokeWidth={2} />}
              </span>
              <span className={it.done ? "text-navy-dark/55 line-through decoration-navy-dark/25" : "text-navy-dark"}>
                {it.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="mb-8 bg-white border border-navy/10 p-5 md:p-6" data-testid="booking-checklist-panel">
      <CollapsibleSection
        allDone={allDone}
        count={`${doneItems} / ${totalItems}`}
        testid="booking-checklist-collapse"
        title={
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            <div>
              <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Kunden-Checkliste</div>
              <div className="text-[12.5px] text-navy-dark/60">
                {isCurrent
                  ? "Persönliche Erledigungen des Kunden — abgehakt bei der Abreise-Planung."
                  : "Persönliche Erledigungen für die nächste Abwesenheit — vom Kunden vorbereitet."}
              </div>
            </div>
          </div>
        }
      >
        <div className="flex flex-wrap gap-8">
          <Section title="Abreise" items={dep} />
          <Section title="Anreise" items={arr} />
        </div>
      </CollapsibleSection>
    </div>
  );
}
